// ** React Imports
import { useEffect, useState } from 'react';

// ** MUI Imports
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box, { BoxProps } from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import Close from 'mdi-material-ui/Close';
import { Organisation } from '../../../types/Organisation';
import { useUpdateItem } from '../../../hooks/organisations.hooks';
import { Alert, Checkbox, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch } from '@mui/material';
import { useGetItems } from '../../../hooks/license-plans';
import SimpleDialog from '../../common/SimpleDialog';
import PageHeader from '../../../materio/@core/components/page-header';
import FormWithLoader from '../../../v2/components/FormWithLoader/FormWithLoader';
import CloseIcon from '@mui/icons-material/Close';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SectionAppBar from '../../../v2/components/Section/SectionAppBar';
import SaveButton from '../../../v2/components/Button/SaveButton';
import StandardButton from '../../../v2/components/Button/StandardButton';

interface Props {
    open: boolean;
    onClose: () => void;
    record: Organisation;
}

const Header = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const schema = yup.object().shape({
    name: yup.string().required(),
});

const availableFields = [
    'firstName',
    'lastName',
    'emailAddress',
    'company',
    'jobTitle',
    'linkedIn',
    'leadLevelId',
    'switchboard',
    'phone',
    'mobile',
    'city',
    'province',
    'countryId',
    'leadSourceId',
    'leadFunctionId',
    'industryId',
    'addressRegionId',
    'leadOwnerId',
];

const dedupFields = ['firstName', 'lastName', 'emailAddress', 'company', 'phone'];

const EditOrganisationDialog = ({ open, onClose, record }: Props) => {
    const { mutateAsync: updateItem } = useUpdateItem();
    const { data: licensePlans } = useGetItems();
    const [fieldsToCheck, setFieldsToCheck] = useState<string[]>([]);

    const [error, setError] = useState({
        isError: false,
        message: '',
    });

    const {
        reset,
        control,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: record,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        reset(record);
        setFieldsToCheck(JSON.parse(record.requiredLeadFields));
    }, [record]);

    const onSubmit = async (data: Organisation) => {
        const { isError, error } = await updateItem({ ...data, requiredLeadFields: JSON.stringify(fieldsToCheck) });

        if (!isError) onClose();

        setError({
            isError,
            message: error,
        });
    };

    const handleToggleRequiredField = (field: string) => {
        const temp = [...fieldsToCheck];

        const optionIndex = temp.findIndex((f) => f === field);

        if (optionIndex === -1) {
            temp.push(field);
        } else {
            temp.splice(optionIndex, 1);
        }

        setFieldsToCheck(temp);
    };

    if (!licensePlans) return null;

    return (
        <SimpleDialog open={open} onClose={onClose} padding={0} fullHeight={false} size="md">
            <Box sx={{ p: 2 }}>
                <SectionAppBar
                    title={`Edit Organization`}
                    subTitle={''}
                    buttons={
                        <>
                            <SaveButton
                                size="small"
                                type="submit"
                                variant="contained"
                                onClick={handleSubmit(onSubmit)}
                                icon={<SaveAltIcon />}
                            >
                                Save
                            </SaveButton>
                            <StandardButton
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={onClose}
                                icon={<CloseIcon />}
                            >
                                Cancel
                            </StandardButton>
                        </>
                    }
                />
                <FormWithLoader onSubmit={handleSubmit(onSubmit)}>
                    <Grid container>
                        <Grid item xs={12}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="name"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Name"
                                            onChange={onChange}
                                            placeholder="Customer Name"
                                            error={Boolean(errors.name)}
                                        />
                                    )}
                                />
                                {errors.name && (
                                    <FormHelperText sx={{ color: 'error.main' }}>{errors.name.message}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <InputLabel>License Plan</InputLabel>
                                <Controller
                                    name="selectedPlanId"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            size="small"
                                            value={value}
                                            label="License Plan"
                                            onChange={onChange}
                                            placeholder="License Plan"
                                            error={Boolean(errors.selectedPlanId)}
                                        >
                                            {licensePlans.map((item) => (
                                                <MenuItem value={item.id}>{item.title}</MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.selectedPlanId && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.selectedPlanId.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <InputLabel>Plan Type</InputLabel>
                                <Controller
                                    name="planType"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            size="small"
                                            value={value}
                                            label="Plan Type"
                                            onChange={onChange}
                                            placeholder="Plan Type"
                                            error={Boolean(errors.planType)}
                                        >
                                            <MenuItem value="monthly">Montly</MenuItem>
                                            <MenuItem value="yearly">Yearly</MenuItem>
                                        </Select>
                                    )}
                                />
                                {errors.planType && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.planType.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid item xs={12} sx={{ mb: 2 }}>
                                <PageHeader
                                    title={<Typography variant="subtitle1">Lead Import Required Fields</Typography>}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {availableFields.sort().map((f) => (
                                    <FormControlLabel
                                        title={
                                            dedupFields.some((r) => r === f) ? 'Required for deduplication checks' : ''
                                        }
                                        control={
                                            <Checkbox
                                                disabled={dedupFields.some((r) => r === f)}
                                                checked={fieldsToCheck.some((s) => s === f)}
                                                onClick={(e) => handleToggleRequiredField(f)}
                                            />
                                        }
                                        label={f}
                                    />
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={6} md={4}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="singleEntity"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                }
                                                label="Single Entity"
                                            />
                                        </FormControl>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="automatePhoneActivity"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                }
                                                label="Automate Phone Activity"
                                            />
                                        </FormControl>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="phoneCountryValidation"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                }
                                                label="Phone Country Validation"
                                            />
                                        </FormControl>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="recordCalls"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                }
                                                label="Record Calls"
                                            />
                                        </FormControl>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="isActive"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                }
                                                label="Active"
                                            />
                                        </FormControl>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}></Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="complianceEmail"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Compliance Email"
                                            onChange={onChange}
                                            placeholder="Compliance Email"
                                            error={Boolean(errors.complianceEmail)}
                                        />
                                    )}
                                />
                                {errors.complianceEmail && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.complianceEmail.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sx={{ mb: 2 }}>
                            <PageHeader title={<Typography variant="subtitle1">Telephony</Typography>} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="twilioAppSid"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Telephony API SID"
                                            onChange={onChange}
                                            placeholder="Telephony API SID"
                                            error={Boolean(errors.twilioAppSid)}
                                        />
                                    )}
                                />
                                {errors.twilioAppSid && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.twilioAppSid.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="twilioApiKey"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Telephony API Key"
                                            onChange={onChange}
                                            placeholder="Telephony API Key"
                                            error={Boolean(errors.twilioApiKey)}
                                        />
                                    )}
                                />
                                {errors.twilioApiKey && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.twilioApiKey.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="twilioApiSecret"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            type="password"
                                            size="small"
                                            value={value}
                                            label="Telephony API Secret"
                                            onChange={onChange}
                                            placeholder="Telephony API Secret"
                                            error={Boolean(errors.twilioApiSecret)}
                                        />
                                    )}
                                />
                                {errors.twilioApiSecret && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.twilioApiSecret.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="twilioAccountSid"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Telephony Account SID"
                                            onChange={onChange}
                                            placeholder="Telephony Account SID"
                                            error={Boolean(errors.twilioAccountSid)}
                                        />
                                    )}
                                />
                                {errors.twilioAccountSid && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.twilioAccountSid.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="twilioAccountToken"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            size="small"
                                            value={value}
                                            label="Telephony Account Token"
                                            onChange={onChange}
                                            placeholder="Telephony Account Token"
                                            error={Boolean(errors.twilioAccountToken)}
                                        />
                                    )}
                                />
                                {errors.twilioAccountToken && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.twilioAccountToken.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth sx={{ mb: 6 }}>
                                <Controller
                                    name="debugTwilioTwiml"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                }
                                                label="Debug Telephony"
                                            />
                                        </FormControl>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </FormWithLoader>

                {error.isError && <Alert color="error">{error.message}</Alert>}
            </Box>
        </SimpleDialog>
    );
};

export default EditOrganisationDialog;
